/**
 * Canonical money formatter used everywhere the app displays a dollar amount.
 *
 * Replaces scattered `.toFixed(2)` / `toLocaleString()` / `Intl.NumberFormat(...)`
 * calls so totals render with consistent thousand-separator commas (e.g.
 * `$16,914.32`, `$1,234,567.89`).
 *
 * Usage:
 *   formatMoney(16914.32)               // "$16,914.32"
 *   formatMoney(1234567, { decimals: 0 }) // "$1,234,567"
 *   formatMoney(1250000, { compact: true }) // "$1.3M"
 *   formatMoney("1234.5", { noSymbol: true }) // "1,234.50"
 *   formatMoney("From $650")            // "From $650"  (returned unchanged)
 *   formatMoney(null)                   // ""           (NOT "$0.00")
 *
 * Two rules this function must never break (Jun 2026):
 *
 *  1. FREE TEXT IS RETURNED AS STORED. Add-on prices are legitimately
 *     non-numeric — "From $650", "Starting at $1,200", "Call for pricing" —
 *     and they are shown to CLIENTS. A non-numeric string comes back exactly
 *     as stored: not parsed, not coerced, and never given a second "$".
 *  2. A MISSING PRICE IS NOT A FREE PRICE. null / undefined / "" return an
 *     empty string, never "$0.00" — the same rule the payment and payout
 *     resolvers already follow. And nothing here can ever emit "$NaN".
 *
 * Options:
 *   - decimals: 0 | 2 (default 2)
 *   - compact:  true → "$1.2M" / "$450K" (for summary bars, not accounting)
 *   - noSymbol: true → omits the leading "$"
 */
const NUMERIC_RE = /^-?(?:\d+(?:\.\d*)?|\.\d+)$/

export function formatMoney(value, opts = {}) {
  const { decimals = 2, compact = false, noSymbol = false } = opts

  // Absence is not zero.
  if (value === null || value === undefined) return ''

  let n
  if (typeof value === 'number') {
    n = value
  } else if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed === '') return ''
    const bare = trimmed.replace(/[$,\s]/g, '')
    // "From $650" / "Call for pricing" — the owner's own words, kept verbatim.
    if (!NUMERIC_RE.test(bare)) return value
    n = parseFloat(bare)
  } else {
    return ''
  }

  // Never "$NaN", never an invented number.
  if (!Number.isFinite(n)) return ''

  if (compact) {
    const abs = Math.abs(n)
    const sign = n < 0 ? '-' : ''
    const sym = noSymbol ? '' : '$'
    if (abs >= 1_000_000) return `${sign}${sym}${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`
    if (abs >= 1_000)     return `${sign}${sym}${(abs / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}K`
    return `${sign}${sym}${abs.toFixed(decimals)}`
  }

  const formatted = new Intl.NumberFormat('en-US', {
    style: noSymbol ? 'decimal' : 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n)
  return formatted
}

export default formatMoney
